import { Link, useLocation } from 'react-router-dom';
import { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';

export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const location = useLocation();
  const { user, profile, signOut } = useAuth();
  const [showNudgeBanner, setShowNudgeBanner] = useState(false);
  const navigate = useNavigate();

  const transparentHeroPages = ['/', '/become-host'];
  const hasTransparentHero = transparentHeroPages.includes(location.pathname);

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const fetchUnreadCount = async () => {
      if (!user?.id) return;
      const { count } = await supabase
        .from('messages')
        .select('*', { count: 'exact', head: true })
        .neq('sender_id', user.id)
        .eq('read', false);
      setUnreadCount(count || 0);
    };
    fetchUnreadCount();
    const interval = setInterval(fetchUnreadCount, 5000);
    return () => clearInterval(interval);
  }, [user?.id]);

  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [isMobileMenuOpen]);

  const isActive = (path: string) => location.pathname === path;
  const shouldShowWhiteText = hasTransparentHero && !isScrolled;

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/');
    } catch (_error: unknown) {
      // silently handle error
    }
  };

  useEffect(() => {
    const checkProfile = async () => {
      if (!user?.id) { setShowNudgeBanner(false); return; }
      const { data } = await supabase.from('profiles').select('phone, street_address').eq('id', user.id).single();
      if (!data?.phone || !data?.street_address) {
        setShowNudgeBanner(true);
      } else {
        setShowNudgeBanner(false);
      }
    };
    checkProfile();
  }, [user?.id]);

  return (
    <>
      {showNudgeBanner && (
        <div className="fixed top-0 left-0 right-0 z-[60] bg-amber-500 text-white px-4 py-2 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm">
            <i className="ri-user-line"></i>
            <span>Complete your profile to book or list a space — it only takes 2 minutes.</span>
          </div>
          <div className="flex items-center gap-3 flex-shrink-0">
            <a href="/settings" className="bg-white text-amber-600 font-semibold text-xs px-3 py-1 rounded-lg hover:bg-amber-50 transition-colors whitespace-nowrap">
              Complete Now
            </a>
            <button onClick={() => setShowNudgeBanner(false)} className="text-white hover:text-amber-200 cursor-pointer">
              <i className="ri-close-line text-lg"></i>
            </button>
          </div>
        </div>
      )}
      <header
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          isScrolled || !hasTransparentHero ? 'bg-white shadow-md' : 'bg-transparent'
        }`}
      >
        <div className="w-full px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center z-50">
              <div className="bg-white rounded-lg px-3 py-2">
                <img
                  src="https://static.readdy.ai/image/ecfc40ffeefdf135692cc83520a4b16c/f58e3285c1424e19fa95f9c3703075da.png"
                  alt="Stashly"
                  className="h-8 sm:h-10"
                />
              </div>
            </Link>

            <nav className="hidden lg:flex items-center gap-6">
              <Link to="/browse" className={`font-medium transition-colors whitespace-nowrap ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-900 hover:text-blue-600'} ${isActive('/browse') ? 'text-blue-600' : ''}`}>Browse</Link>
              <Link to="/how-it-works" className={`font-medium transition-colors whitespace-nowrap ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-900 hover:text-blue-600'} ${isActive('/how-it-works') ? 'text-blue-600' : ''}`}>How It Works</Link>
              <Link to="/pricing" className={`font-medium transition-colors whitespace-nowrap ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-900 hover:text-blue-600'} ${isActive('/pricing') ? 'text-blue-600' : ''}`}>Pricing</Link>
              <Link to="/safety" className={`font-medium transition-colors whitespace-nowrap ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-900 hover:text-blue-600'} ${isActive('/safety') ? 'text-blue-600' : ''}`}>Safety</Link>
              <Link to="/faq" className={`font-medium transition-colors whitespace-nowrap ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-900 hover:text-blue-600'} ${isActive('/faq') ? 'text-blue-600' : ''}`}>FAQs</Link>
              <Link to="/contact" className={`font-medium transition-colors whitespace-nowrap ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-900 hover:text-blue-600'} ${isActive('/contact') ? 'text-blue-600' : ''}`}>Contact</Link>
            </nav>

            <div className="hidden lg:flex items-center gap-3">
              {user ? (
                <>
                  <Link to="/list-space" className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-add-circle-line text-lg"></i>List Your Space
                  </Link>
                  <Link to="/browse" className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-search-line text-lg"></i>Find a Space
                  </Link>
                  <button onClick={() => navigate('/messages')} className={`relative p-2 transition-colors cursor-pointer ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-600 hover:text-[#6366F1]'}`}>
                    <i className="ri-notification-3-line text-xl"></i>
                    {unreadCount > 0 && (
                      <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">
                        {unreadCount > 9 ? '9+' : unreadCount}
                      </span>
                    )}
                  </button>
                  <button onClick={() => navigate('/favourites')} className={`relative p-2 transition-colors cursor-pointer ${shouldShowWhiteText ? 'text-white hover:text-red-300' : 'text-gray-600 hover:text-red-500'}`}>
                    <i className="ri-heart-line text-xl"></i>
                  </button>
                  <Link to="/profile" className={`flex items-center gap-2 px-4 py-2.5 font-semibold transition-colors cursor-pointer whitespace-nowrap ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-900 hover:text-blue-600'}`}>
                    <i className="ri-user-line text-lg"></i>
                    {profile?.full_name || 'Profile'}
                  </Link>
                  <button onClick={handleSignOut} className={`flex items-center gap-2 px-4 py-2.5 font-semibold transition-colors cursor-pointer whitespace-nowrap ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-900 hover:text-blue-600'}`}>
                    <i className="ri-logout-box-line text-lg"></i>Log Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/list-space" className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-add-circle-line text-lg"></i>List Your Space
                  </Link>
                  <Link to="/browse" className="flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-search-line text-lg"></i>Find a Space
                  </Link>
                  <Link to="/login" className={`flex items-center gap-2 px-4 py-2.5 font-semibold transition-colors cursor-pointer whitespace-nowrap ${shouldShowWhiteText ? 'text-white hover:text-blue-200' : 'text-gray-900 hover:text-blue-600'}`}>
                    <i className="ri-login-box-line text-lg"></i>Log In
                  </Link>
                  <Link to="/signup" className="flex items-center gap-2 px-5 py-2.5 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors cursor-pointer whitespace-nowrap">
                    <i className="ri-user-add-line text-lg"></i>Sign Up
                  </Link>
                </>
              )}
            </div>

            {!user && !isMobileMenuOpen && (
              <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3 lg:hidden">
                <Link to="/login" className="px-5 py-3 text-base font-semibold text-white bg-blue-600 rounded-xl hover:bg-blue-700 transition-colors whitespace-nowrap">Log In</Link>
                <Link to="/signup" className="px-5 py-3 text-base font-semibold text-white bg-gray-900 rounded-xl hover:bg-gray-800 transition-colors whitespace-nowrap">Sign Up</Link>
              </div>
            )}

            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className={`lg:hidden z-50 p-2 rounded-lg transition-colors ${isMobileMenuOpen ? 'bg-gray-900 text-white' : 'bg-white text-gray-900 hover:bg-gray-100 shadow-md'}`}
              aria-label="Toggle menu"
            >
              {isMobileMenuOpen ? <i className="ri-close-line text-2xl"></i> : <i className="ri-menu-line text-2xl"></i>}
            </button>
          </div>
        </div>
      </header>

      {isMobileMenuOpen && (
        <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setIsMobileMenuOpen(false)} />
      )}

      <div className={`fixed top-0 right-0 h-full w-80 max-w-[85vw] bg-white shadow-2xl z-40 transform transition-transform duration-300 ease-in-out lg:hidden ${isMobileMenuOpen ? 'translate-x-0' : 'translate-x-full'}`}>
        <div className="flex flex-col h-full overflow-y-auto">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-xl font-bold text-gray-900">Menu</h2>
          </div>
          <div className="flex-1 p-6">
            {user ? (
              <div className="mb-6 pb-6 border-b border-gray-200">
                <Link to="/profile" className="flex items-center gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
                  <div className="w-12 h-12 bg-blue-600 rounded-full flex items-center justify-center">
                    <i className="ri-user-line text-white text-xl"></i>
                  </div>
                  <div>
                    <p className="font-semibold text-gray-900">{profile?.full_name || 'My Profile'}</p>
                    <p className="text-sm text-gray-600">View & edit profile</p>
                  </div>
                </Link>
                {unreadCount > 0 && (
                  <button onClick={() => { navigate('/messages'); setIsMobileMenuOpen(false); }} className="w-full flex items-center gap-3 px-4 py-3 bg-red-50 border border-red-100 rounded-lg mt-3 cursor-pointer">
                    <div className="relative">
                      <i className="ri-notification-3-line text-xl text-red-500"></i>
                      <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-bold rounded-full w-5 h-5 flex items-center justify-center">{unreadCount}</span>
                    </div>
                    <span className="font-semibold text-red-600">{unreadCount} unread message{unreadCount > 1 ? 's' : ''}</span>
                  </button>
                )}
              </div>
            ) : (
              <div className="mb-6 pb-6 border-b border-gray-200 space-y-3">
                <Link to="/login" className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-blue-600 text-white font-semibold rounded-lg hover:bg-blue-700 transition-colors">
                  <i className="ri-login-box-line text-lg"></i>Log In
                </Link>
                <Link to="/signup" className="flex items-center justify-center gap-2 w-full px-5 py-3 bg-gray-900 text-white font-semibold rounded-lg hover:bg-gray-800 transition-colors">
                  <i className="ri-user-add-line text-lg"></i>Sign Up
                </Link>
              </div>
            )}

            <div className="mb-6 pb-6 border-b border-gray-200 space-y-2">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Quick Actions</h3>
              <Link to="/list-space" className="flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-blue-600">
                <i className="ri-add-circle-line text-xl"></i><span className="font-medium">List Your Space</span>
              </Link>
              <Link to="/browse" className="flex items-center gap-3 p-3 rounded-lg hover:bg-blue-50 transition-colors text-blue-600">
                <i className="ri-search-line text-xl"></i><span className="font-medium">Find a Space</span>
              </Link>
            </div>

            <nav className="mb-6 pb-6 border-b border-gray-200 space-y-1">
              <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">Navigation</h3>
              <Link to="/browse" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/browse') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                <i className="ri-search-2-line text-xl"></i><span className="font-medium">Browse Spaces</span>
              </Link>
              <Link to="/how-it-works" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/how-it-works') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                <i className="ri-lightbulb-line text-xl"></i><span className="font-medium">How It Works</span>
              </Link>
              <Link to="/pricing" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/pricing') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                <i className="ri-price-tag-3-line text-xl"></i><span className="font-medium">Pricing</span>
              </Link>
              <Link to="/safety" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/safety') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                <i className="ri-shield-check-line text-xl"></i><span className="font-medium">Safety</span>
              </Link>
              <Link to="/faq" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/faq') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                <i className="ri-question-line text-xl"></i><span className="font-medium">FAQs</span>
              </Link>
              <Link to="/contact" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/contact') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                <i className="ri-mail-line text-xl"></i><span className="font-medium">Contact</span>
              </Link>
            </nav>

            {user && (
              <nav className="mb-6 pb-6 border-b border-gray-200 space-y-1">
                <h3 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-3">My Account</h3>
                <Link to="/my-listings" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/my-listings') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-list-check text-xl"></i><span className="font-medium">My Listings</span>
                </Link>
                <Link to="/bookings" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/bookings') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-calendar-check-line text-xl"></i><span className="font-medium">My Bookings</span>
                </Link>
                <Link to="/messages" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/messages') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-message-3-line text-xl"></i><span className="font-medium">Messages</span>
                </Link>
                <Link to="/favourites" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/favourites') ? 'bg-red-50 text-red-500' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-heart-line text-xl text-red-500"></i><span className="font-medium">My Favourites</span>
                </Link>
                <Link to="/earnings" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/earnings') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-money-dollar-circle-line text-xl"></i><span className="font-medium">Earnings</span>
                </Link>
                <Link to="/settings" className={`flex items-center gap-3 p-3 rounded-lg transition-colors ${isActive('/settings') ? 'bg-blue-50 text-blue-600' : 'text-gray-700 hover:bg-gray-50'}`}>
                  <i className="ri-settings-3-line text-xl"></i><span className="font-medium">Settings</span>
                </Link>
              </nav>
            )}

            {user && (
              <button onClick={handleSignOut} className="flex items-center gap-3 w-full p-3 rounded-lg text-red-600 hover:bg-red-50 transition-colors">
                <i className="ri-logout-box-line text-xl"></i><span className="font-medium">Log Out</span>
              </button>
            )}
          </div>
          <div className="p-6 border-t border-gray-200 bg-gray-50">
            <p className="text-xs text-gray-600 text-center">© 2024 Stashly. All rights reserved.</p>
          </div>
        </div>
      </div>
    </>
  );
}
